import { api } from '@/shared/lib/api'
import type { Project } from '@workspace/lib/api'
import { useMasterType } from '@/shared/hooks/useMasterType'
import { MASTER_TYPE_SLUG } from '@/shared/constants/master-types'
import { FormDialog } from '@/shared/components/dialogs/FormDialog'
import { FilterDropdown } from '@/shared/components/FilterDropdown'
import TableDataFooter from '@/shared/components/TableDataFooter'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { hasPermissionSync, requirePermissionBeforeLoad } from '@/shared/lib/authorization/routeAuth'
import { createColumnHelper } from '@tanstack/react-table'
import { Badge } from '@workspace/ui/components/Badge'
import { Button } from '@workspace/ui/components/Button'
import { DataTable, type DataTableSorting } from '@workspace/ui/components/DataTable'
import { Menu, MenuHeader, MenuItem, MenuPopover, MenuTrigger } from '@workspace/ui/components/Menu'
import { BsSearchField } from '@workspace/ui/components/Searchfield'
import { toast } from '@workspace/ui/components/Sonner'
import dayjs from 'dayjs'
import { ChevronDown, FileDown, Loader2, MoreHorizontal, PlusIcon, Upload, Star } from 'lucide-react'
import React, { useEffect, useMemo, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { confirm } from '@workspace/ui/components/ConfirmDialog'
import { auditLogHelpers } from '@/shared/lib/audit-log'
import MenuDropdown from '@/shared/components/MenuDropdown'
import { useImportProjects } from '@/shared/hooks/projects/useImportProjects'
import { useExportReport } from '@/shared/hooks/projects/useExportReport'
import { useCan } from '@/shared/lib/authorization'
import { z } from 'zod'

const searchSchema = z.object({
    q: z.string().optional(),
    page: z.number().catch(1).optional(),
    pageSize: z.number().catch(10).optional(),
    regionValues: z.string().optional(),
    functionalDepartmentValues: z.string().optional(),
    areaValues: z.string().optional(),
    statusValues: z.string().optional(),
    managerValues: z.string().optional(),
    isKeyProject: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.string().optional(),
})

export const Route = createFileRoute('/projects/')({
    validateSearch: searchSchema,
    component: ProjectsPage,
    beforeLoad: requirePermissionBeforeLoad('view', 'project'),
})

const columnHelper = createColumnHelper<ProjectSimple>()

function ProjectsPage() {
    const { t } = useTranslation()
    const searchParams = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })
    const queryClient = useQueryClient()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})

    const searchQuery = searchParams.q || ''
    const currentPage = searchParams.page || 1
    const pageSize = searchParams.pageSize || 10
    const regionValues = searchParams.regionValues ? searchParams.regionValues.split(',') : []
    const functionalDepartmentValues = searchParams.functionalDepartmentValues
        ? searchParams.functionalDepartmentValues.split(',')
        : []
    const areaValues = searchParams.areaValues ? searchParams.areaValues.split(',') : []
    const statusValues = searchParams.statusValues ? searchParams.statusValues.split(',') : []
    const managerValues = searchParams.managerValues ? searchParams.managerValues.split(',') : []
    const isKeyProjectParam = searchParams.isKeyProject
    const sorting = searchParams.sortBy
        ? { sortBy: searchParams.sortBy, sortDirection: (searchParams.sortDirection as 'asc' | 'desc') || 'asc' }
        : null

    const updateSearch = (newSearch: Record<string, any>) => {
        navigate({ search: (prev: any) => ({ ...prev, ...newSearch }), replace: true })
    }

    const setSearchQuery = (q: string) => updateSearch({ q, page: 1 })
    const setCurrentPage = (page: number) => updateSearch({ page })
    const setPageSize = (pageSize: number) => updateSearch({ pageSize, page: 1 })
    const setRegionValues = (values: string[]) => {
        updateSearch({ regionValues: values.length > 0 ? values.join(',') : undefined, page: 1 })
    }
    const setFunctionalDepartmentValues = (values: string[]) =>
        updateSearch({ functionalDepartmentValues: values.length > 0 ? values.join(',') : undefined, page: 1 })
    const setAreaValues = (values: string[]) =>
        updateSearch({ areaValues: values.length > 0 ? values.join(',') : undefined, page: 1 })
    const setStatusValues = (values: string[]) =>
        updateSearch({ statusValues: values.length > 0 ? values.join(',') : undefined, page: 1 })
    const setManagerValues = (values: string[]) =>
        updateSearch({ managerValues: values.length > 0 ? values.join(',') : undefined, page: 1 })
    const setIsKeyProject = (values: string[]) =>
        updateSearch({ isKeyProject: values.length > 0 ? values[values.length - 1] : undefined, page: 1 })
    const setSorting = (sort: DataTableSorting | null) => {
        if (!sort) {
            updateSearch({ sortBy: undefined, sortDirection: undefined, page: 1 })
        } else {
            updateSearch({ sortBy: sort.sortBy, sortDirection: sort.sortDirection, page: 1 })
        }
    }

    const [projectAction, setProjectAction] = useState<{
        project: Project
        action: 'view' | 'delete'
    } | null>(null)

    // Debounce search query
    const debouncedSearch = useDebounce(searchQuery, 300)

    const canOperateProject = hasPermissionSync('operate', 'project')
    const canExportProject = hasPermissionSync('export', 'project')

    // Fetch regions for filter dropdown
    const { data: regionsData } = useQuery({
        queryKey: ['regions'],
        queryFn: () => api.region.list({ page: 1, take: 100 }),
    })

    // Fetch areas for filter dropdown
    const { data: areasData } = useQuery({
        queryKey: ['areas'],
        queryFn: () => api.area.list({ page: 1, take: 100 }),
    })

    const { data: masterType } = useMasterType({ enabled: true })

    // Fetch functional departments for filter dropdown
    const { data: functionalDepartmentsData } = useQuery({
        queryKey: ['functionalDepartments'],
        queryFn: () =>
            api.masterData.list({
                page: 1,
                take: 100,
                masterTypeId:
                    masterType?.data?.find(mt => mt.code === MASTER_TYPE_SLUG.FUNCTIONAL_DEPARTMENT)?.id || '',
            }),
        enabled: !!masterType,
    })

    // Fetch users for manager filter dropdown
    const { data: usersData } = useQuery({
        queryKey: ['users'],
        queryFn: () => api.user.listAll({ page: 1, take: 100 }),
    })

    // Auto-prune area, functional department and manager filters when region/area selection changes
    useEffect(() => {
        if (!areasData?.data || !functionalDepartmentsData?.data || !usersData?.data) return

        let newAreaValues = areaValues
        let newFdValues = functionalDepartmentValues
        let newManagerValues = managerValues

        if (regionValues.length === 0) {
            newAreaValues = []
            newFdValues = []
        } else {
            const validAreaIds = new Set(
                areasData.data.filter((a: any) => regionValues.includes(a.region?.id)).map((a: any) => a.id),
            )

            const validFdIds = new Set(
                functionalDepartmentsData.data
                    .filter((fd: any) => {
                        const rId = fd?.regionId || fd?.region?.id
                        return rId && regionValues.includes(rId)
                    })
                    .map((fd: any) => fd.id),
            )

            newAreaValues = areaValues.filter(id => validAreaIds.has(id))
            newFdValues = functionalDepartmentValues.filter(id => validFdIds.has(id))
        }

        if (regionValues.length > 0 || areaValues.length > 0) {
            const validManagerIds = new Set(
                usersData.data
                    .filter((user: any) => {
                        const matchRegion =
                            regionValues.length === 0 || (user.region && regionValues.includes(user.region.id))
                        const matchArea =
                            areaValues.length === 0 ||
                            (user.areas && user.areas.some((a: any) => areaValues.includes(a.id)))
                        return matchRegion && matchArea
                    })
                    .map((user: any) => user.id),
            )
            newManagerValues = managerValues.filter(id => validManagerIds.has(id))
        }

        if (
            newAreaValues.length !== areaValues.length ||
            newFdValues.length !== functionalDepartmentValues.length ||
            newManagerValues.length !== managerValues.length
        ) {
            updateSearch({
                areaValues: newAreaValues.length > 0 ? newAreaValues.join(',') : undefined,
                functionalDepartmentValues: newFdValues.length > 0 ? newFdValues.join(',') : undefined,
                managerValues: newManagerValues.length > 0 ? newManagerValues.join(',') : undefined,
            })
        }
    }, [
        regionValues,
        areaValues,
        functionalDepartmentValues,
        managerValues,
        areasData,
        functionalDepartmentsData,
        usersData,
    ])

    // Fetch projects with search + pagination
    const { data, isLoading, refetch } = useQuery({
        queryKey: [
            'projects',
            debouncedSearch,
            regionValues,
            functionalDepartmentValues,
            areaValues,
            statusValues,
            managerValues,
            isKeyProjectParam,
            currentPage,
            pageSize,
            sorting,
        ],
        queryFn: () =>
            api.project.list({
                page: currentPage,
                take: pageSize,
                q: debouncedSearch || undefined,
                statuses: statusValues.length > 0 ? statusValues.join(',') : undefined,
                regionIds: regionValues.length > 0 ? regionValues.join(',') : undefined,
                functionalDepartmentIds:
                    functionalDepartmentValues.length > 0 ? functionalDepartmentValues.join(',') : undefined,
                areaIds: areaValues.length > 0 ? areaValues.join(',') : undefined,
                managedByIds: managerValues.length > 0 ? managerValues.join(',') : undefined,
                isKeyProject: isKeyProjectParam === 'true' ? true : isKeyProjectParam === 'false' ? false : undefined,
                sortBy: sorting?.sortBy,
                order: sorting?.sortDirection?.toUpperCase() as 'ASC' | 'DESC' | undefined,
            }),
    })

    // Delete project mutation
    const deleteMutation = useMutation({
        mutationFn: async (projectIds: string[]) => {
            // Delete projects in parallel
            await Promise.all(projectIds.map(id => api.project.delete(id)))
        },
        onSuccess: () => {
            toast.success({
                title: t('projects.list.deleteSuccess.title'),
                description: t('projects.list.deleteSuccess.description'),
            })
            // Refresh the list and clear selection
            refetch()
            setRowSelection({})
        },
        onError: error => {
            toast.error({
                title: t('projects.list.deleteError.title'),
                description: t('projects.list.deleteError.description'),
            })
            console.error('Delete error:', error)
        },
    })

    const importMutation = useImportProjects()
    const exportReportMutation = useExportReport()

    const handleExportReport = async () => {
        const selectedProjectIds = Object.keys(rowSelection)
        if (selectedProjectIds.length === 0) return

        await exportReportMutation.mutateAsync({
            projectIds: selectedProjectIds,
        })
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            importMutation.mutate(file, {
                onSettled: () => {
                    if (fileInputRef.current) {
                        fileInputRef.current.value = ''
                    }
                },
            })
        }
    }

    // Options for dropdowns (dynamic from API)
    const regionOptions = useMemo(
        () => regionsData?.data.map(region => ({ id: region.id, name: region.name })) || [],
        [regionsData],
    )

    const areaOptions = useMemo(() => {
        const allAreas = areasData?.data || []
        if (regionValues.length === 0) return allAreas.map((area: any) => ({ id: area.id, name: area.name }))
        return allAreas
            .filter((area: any) => regionValues.includes(area.region?.id))
            .map((area: any) => ({ id: area.id, name: area.name }))
    }, [areasData, regionValues])

    const functionalDepartmentOptions = useMemo(() => {
        let filteredFds = functionalDepartmentsData?.data || []
        if (regionValues.length > 0) {
            filteredFds = filteredFds.filter(fd => {
                const rId = fd.regionId || fd.region?.id
                return !!(rId && regionValues.includes(rId))
            })
        }
        return filteredFds.map(fd => ({ id: fd.id, name: fd.name }))
    }, [functionalDepartmentsData, regionValues])

    const managerOptions = useMemo(() => {
        let filteredUsers = usersData?.data || []

        if (regionValues.length > 0) {
            filteredUsers = filteredUsers.filter(user => user.region && regionValues.includes(user.region.id))
        }

        if (areaValues.length > 0) {
            filteredUsers = filteredUsers.filter(
                user => user.areas && user.areas.some(area => areaValues.includes(area.id)),
            )
        }

        return filteredUsers.map(user => ({ id: user.id, name: user.fullName }))
    }, [usersData, regionValues, areaValues])

    const statusOptions = useMemo(
        () => [
            { id: 'PREPARING', name: t('projects.list.status.preparation') },
            { id: 'IN_PROGRESS', name: t('projects.list.status.inProgress') },
            { id: 'COMPLETED', name: t('projects.list.status.completed') },
            { id: 'ON_HOLD', name: t('projects.list.status.onHold') },
            { id: 'CANCELLED', name: t('projects.list.status.cancelled') },
        ],
        [t],
    )

    const keyProjectOptions = useMemo(
        () => [
            { id: 'true', name: t('projects.list.filters.keyProjects', 'Dự án trọng điểm') },
            { id: 'false', name: t('projects.list.filters.otherProjects', 'Các dự án còn lại') },
        ],
        [t],
    )

    const handleDelete = async () => {
        if (!projectAction?.project) return
        toast.success({
            title: t('projects.list.deleteSuccess.title'),
            description: t('projects.list.deleteSuccess.description'),
        })
        setProjectAction(null)
    }

    const handleDeleteMultiple = async () => {
        const projectIds = Object.keys(rowSelection)
        if (projectIds.length === 0) return

        await deleteMutation.mutateAsync(projectIds)
    }

    const handleKeydown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            if (currentPage !== 1) setCurrentPage(1)
            else await refetch()
        }
    }

    const totalResults = data?.meta.itemCount || 0

    const columns = [
        columnHelper.display({
            id: 'custom-actions',
            header: '',
            size: 48,
            enableSorting: false,
            enableResizing: false,
            meta: { className: '!max-w-[48px] !min-w-[48px] !w-[48px]' },
            cell: ({ row }) => {
                const project = row.original
                return (
                    <div onClick={e => e.stopPropagation()}>
                        <MenuTrigger>
                            <Button variant="outline" size="iconSm" className="rounded-full border-foreground h-5 w-5">
                                <MoreHorizontal className="size-3!" />
                            </Button>
                            <MenuPopover className="w-[120px]">
                                <Menu>
                                    <MenuHeader className="text-foreground">{t('common.actions.operation')}</MenuHeader>
                                    <MenuItem
                                        onAction={() => {
                                            navigate({ to: `/projects/${project.id}` })
                                        }}
                                        className="cursor-pointer"
                                    >
                                        {t('common.actions.view')}
                                    </MenuItem>
                                    <MenuItem
                                        onAction={() => {
                                            confirm({
                                                title: t('projects.list.dialogs.delete.title'),
                                                description: t('projects.list.dialogs.delete.description'),
                                                action: {
                                                    label: t('common.actions.delete'),
                                                    onClick: async () => {
                                                        await deleteMutation.mutateAsync([row.original.id])
                                                    },
                                                },
                                                cancel: {
                                                    label: t('common.actions.cancel'),
                                                },
                                                variant: 'destructive',
                                            })
                                        }}
                                        className="cursor-pointer text-destructive"
                                    >
                                        {t('common.actions.delete')}
                                    </MenuItem>
                                </Menu>
                            </MenuPopover>
                        </MenuTrigger>
                    </div>
                )
            },
        }),
        columnHelper.accessor('code', {
            header: t('projects.list.table.code'),
            size: 140,
            enableSorting: false,
            enableResizing: false,
            meta: { headerClassName: 'font-bold text-secondary-foreground' },
            cell: ({ getValue }) => {
                return <span className="text-sm text-foreground">{getValue()}</span>
            },
        }),
        columnHelper.accessor('name', {
            header: t('projects.list.table.name'),
            size: 280,
            minSize: 180,
            maxSize: 600,
            meta: {
                headerClassName: 'font-bold text-secondary-foreground',
                className: 'whitespace-normal',
            },
            cell: ({ row }) => {
                const project = row.original
                return (
                    <div className="flex items-center justify-between gap-2">
                        <span
                            className="text-sm text-foreground whitespace-normal break-words leading-snug"
                            title={project.name || undefined}
                        >
                            {project.name || '-'}
                        </span>
                        {project.isKeyProject && (
                            <span title="Dự án trọng điểm" className="flex-shrink-0">
                                <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                            </span>
                        )}
                    </div>
                )
            },
        }),
        columnHelper.display({
            id: 'region-area',
            header: t('projects.list.table.regionArea'),
            size: 130,
            enableResizing: false,
            meta: { headerClassName: 'font-bold text-secondary-foreground' },
            cell: ({ row }) => {
                const region = row.original.region?.name
                const area = row.original.area?.name
                return (
                    <div className="flex flex-col py-1">
                        <span
                            className="text-sm font-medium text-foreground leading-5 truncate max-w-full"
                            title={region}
                        >
                            {region || '-'}
                        </span>
                        <span className="text-xs text-muted-foreground leading-4 truncate max-w-full" title={area}>
                            {area || '-'}
                        </span>
                    </div>
                )
            },
        }),
        columnHelper.accessor('status', {
            header: t('projects.list.table.status'),
            size: 130,
            enableSorting: false,
            enableResizing: false,
            meta: { headerClassName: 'font-bold text-secondary-foreground' },
            cell: ({ getValue }) => {
                const status = getValue()

                const statusConfig: Record<
                    string,
                    { label: string; backgroundColor: string; variant: 'default' | 'outline' }
                > = {
                    PREPARING: {
                        label: t('projects.list.status.preparation'),
                        backgroundColor: 'var(--blue)',
                        variant: 'default',
                    },
                    IN_PROGRESS: {
                        label: t('projects.list.status.inProgress'),
                        backgroundColor: 'var(--background-yellow)',
                        variant: 'default',
                    },
                    COMPLETED: {
                        label: t('projects.list.status.completed'),
                        backgroundColor: 'var(--color-green-500)',
                        variant: 'default',
                    },
                    CANCELLED: {
                        label: t('projects.list.status.cancelled'),
                        backgroundColor: 'var(--color-red-500)',
                        variant: 'default',
                    },
                    ON_HOLD: {
                        label: t('projects.list.status.onHold'),
                        backgroundColor: 'var(--color-orange-500)',
                        variant: 'default',
                    },
                }

                const config = statusConfig[status] || {
                    label: t('projects.list.status.preparation'),
                    backgroundColor: 'var(--blue)',
                    variant: 'default',
                }

                return (
                    <Badge
                        className="px-2 py-0.5 text-xs font-medium rounded-full"
                        style={{
                            backgroundColor: config.backgroundColor,
                            color: 'white',
                        }}
                        title={config.label}
                    >
                        {config.label}
                    </Badge>
                )
            },
        }),
        columnHelper.display({
            id: 'manager',
            header: t('projects.list.table.manager'),
            size: 180,
            enableResizing: false,
            meta: { headerClassName: 'font-bold text-secondary-foreground' },
            cell: ({ row }) => {
                return <span className="text-sm text-foreground">{row.original.manager?.fullName || '-'}</span>
            },
        }),
        columnHelper.display({
            id: 'progress',
            header: t('projects.list.table.progress'),
            size: 160,
            enableResizing: false,
            meta: { headerClassName: 'font-bold text-secondary-foreground' },
            cell: ({ row }) => {
                const progress = row.original.completionPercentage || 0
                return (
                    <div className="flex items-center gap-1">
                        <div className="flex-1 h-2 bg-border rounded-md overflow-hidden">
                            <div
                                className="h-full bg-background-muted rounded-md transition-all"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        <span className="text-sm text-foreground w-10 text-left">{progress}%</span>
                    </div>
                )
            },
        }),
        columnHelper.accessor('createdAt', {
            header: t('projects.list.table.createdAt'),
            size: 140,
            enableResizing: false,
            meta: { headerClassName: 'font-bold text-secondary-foreground' },
            cell: ({ getValue }) => {
                const value = getValue()
                return (
                    <span className="text-sm text-foreground">
                        {value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'}
                    </span>
                )
            },
        }),
    ]

    const handleDownloadTemplate = () => {
        const link = document.createElement('a')
        link.href = '/files/template_project.xlsx'
        link.download = 'Mẫu dự án.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    if (importMutation.isPending) {
        return (
            <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 rounded-lg bg-background/80 backdrop-blur-sm">
                <Loader2 className="size-10 animate-spin text-primary" />
                <p className="text-sm font-medium text-muted-foreground">
                    {t('templates.create.importing', 'Đang xử lý tệp...')}
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-4 max-lg:gap-2 h-[calc(100vh-150px)] min-h-[500px] overflow-hidden">
            <input type="file" ref={fileInputRef} className="hidden" accept=".xlsx, .xls" onChange={handleFileChange} />
            <div className="flex items-center justify-between max-lg:flex-col max-lg:items-start max-lg:gap-2 shrink-0">
                <h1 className="text-2xl font-bold">{t('projects.list.title')}</h1>
                <div className="flex items-center gap-4 max-lg:w-full max-lg:gap-2">
                    <Button
                        hidden={!canExportProject}
                        variant="outline"
                        className="group gap-2 border-border-medium max-lg:w-full"
                        onPress={handleExportReport}
                        isDisabled={Object.keys(rowSelection).length === 0 || exportReportMutation.isPending}
                    >
                        {exportReportMutation.isPending ? (
                            <Loader2 className="size-4 animate-spin" />
                        ) : (
                            t('projects.list.exportReport')
                        )}
                    </Button>
                    <MenuDropdown
                        triggerClassName="gap-2 border-border-medium max-lg:w-full"
                        actions={[
                            {
                                label: t('projects.list.uploadButton'),
                                icon: <Upload size={18} strokeWidth={1.5} />,
                                onAction: () => fileInputRef.current?.click(),
                                disabled: importMutation.isPending,
                            },
                            {
                                label: t('projects.list.downloadTemplate'),
                                icon: <img src="/file-download.svg" alt="Download" className="size-5" />,
                                onAction: handleDownloadTemplate,
                            },
                        ]}
                    >
                        <Button
                            variant="outline"
                            className="group gap-2 border-border-medium max-lg:w-full"
                            hidden={!useCan('operate', 'project')}
                        >
                            {t('projects.list.dataButton')}
                            <ChevronDown
                                size={20}
                                strokeWidth={1.5}
                                className="transition-transform duration-200 group-aria-expanded:rotate-180"
                            />
                        </Button>
                    </MenuDropdown>
                    <Button
                        variant="default"
                        className="gap-2 bg-background-accent text-text-accent hover:bg-background-accent/90 max-lg:w-full"
                        hidden={!useCan('operate', 'project')}
                        onPress={() => navigate({ to: '/projects/new' })}
                    >
                        <PlusIcon size={20} strokeWidth={1.5} />
                        {t('projects.list.createButton')}
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex gap-4 max-lg:gap-2 max-lg:flex-wrap shrink-0">
                <BsSearchField
                    autoTrim
                    className="h-10 text-sm bg-background rounded-lg w-80 max-lg:w-full"
                    containerClassName="max-lg:w-full"
                    placeholder={t('projects.list.searchPlaceholder')}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e)}
                    onKeyDown={handleKeydown}
                />
                <FilterDropdown
                    placeholder={t('projects.list.filters.regionPlaceholder')}
                    inputPlaceholder={t('projects.list.filters.regionPlaceholder')}
                    options={regionOptions}
                    selectedValues={regionValues}
                    onChange={setRegionValues}
                    containerClassName="max-lg:flex-1"
                    className="max-lg:w-full"
                />
                <FilterDropdown
                    placeholder={t('projects.list.filters.areaPlaceholder')}
                    inputPlaceholder={t('projects.list.filters.areaPlaceholder')}
                    options={areaOptions}
                    selectedValues={areaValues}
                    onChange={setAreaValues}
                    containerClassName="max-lg:flex-1"
                    className="max-lg:w-full"
                    isDisabled={!regionValues || regionValues.length === 0}
                    disabledTooltip={t('common.filters.requireRegionFirst')}
                />
                <FilterDropdown
                    placeholder={t('projects.list.filters.functionalDepartmentPlaceholder')}
                    inputPlaceholder={t('projects.list.filters.functionalDepartmentPlaceholder')}
                    options={functionalDepartmentOptions}
                    selectedValues={functionalDepartmentValues}
                    onChange={setFunctionalDepartmentValues}
                    containerClassName="max-lg:flex-1"
                    className="max-lg:w-full"
                />
                <FilterDropdown
                    placeholder={t('projects.list.filters.statusPlaceholder')}
                    inputPlaceholder={t('projects.list.filters.statusPlaceholder')}
                    options={statusOptions}
                    selectedValues={statusValues}
                    onChange={setStatusValues}
                    containerClassName="max-lg:flex-1"
                    className="max-lg:w-full"
                />
                <FilterDropdown
                    placeholder={t('projects.list.filters.managerPlaceholder')}
                    inputPlaceholder={t('projects.list.filters.managerPlaceholder')}
                    options={managerOptions}
                    selectedValues={managerValues}
                    onChange={setManagerValues}
                    containerClassName="max-lg:flex-1"
                    className="max-lg:w-full"
                />
                <FilterDropdown
                    placeholder={t('projects.list.filters.keyProjectPlaceholder', 'Dự án trọng điểm')}
                    inputPlaceholder={t('projects.list.filters.keyProjectPlaceholder', 'Dự án trọng điểm')}
                    options={keyProjectOptions}
                    selectedValues={isKeyProjectParam ? [isKeyProjectParam] : []}
                    onChange={setIsKeyProject}
                    containerClassName="max-lg:flex-1"
                    className="max-lg:w-full"
                />
            </div>

            {/* Table */}
            <DataTable
                data={data?.data || []}
                columns={columns}
                enableRowSelection={true}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                enableSorting={true}
                sorting={sorting}
                setSorting={setSorting}
                isLoading={isLoading}
                emptyString={t('common.emptyState')}
                onRowClick={project => navigate({ to: `/projects/${project.id}` })}
                containerClassName="flex-1 overflow-y-auto min-h-0"
                enableColumnResizing={true}
            />

            {/* Footer */}
            <div className="shrink-0">
                <TableDataFooter
                    selectedCount={Object.keys(rowSelection).length}
                    onDelete={handleDeleteMultiple}
                    deleteDisabled={Object.keys(rowSelection).length === 0 || deleteMutation.isPending}
                    isLoading={deleteMutation.isPending}
                    totalCount={totalResults}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalPages={Math.ceil(totalResults / pageSize)}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={value => setPageSize(value || 10)}
                />
            </div>

            {/* Dialogs */}
            <ProjectActionDialogs
                projectAction={projectAction}
                setProjectAction={setProjectAction}
                handleDelete={handleDelete}
            />
        </div>
    )
}

type ProjectActionDialogsProps = {
    projectAction: { project: Project; action: 'view' | 'delete' } | null
    setProjectAction: (action: { project: Project; action: 'view' | 'delete' } | null) => void
    handleDelete: () => void
}

const ProjectActionDialogs = React.memo(function ProjectActionDialogs({
    projectAction,
    setProjectAction,
    handleDelete,
}: ProjectActionDialogsProps) {
    const { t } = useTranslation()

    if (!projectAction) {
        return null
    }

    const { action, project } = projectAction
    const onOpenChange = () => setProjectAction(null)

    return (
        <>
            {/* View Dialog - You can implement later */}
            {action === 'view' && (
                // TODO(BA): Implement view dialog content once ready
                <FormDialog
                    open={true}
                    onOpenChange={onOpenChange}
                    title={t('projects.list.dialogs.view.title')}
                    description={t('projects.list.dialogs.view.description', { name: project.description })}
                    primaryActionLabel={t('projects.list.dialogs.view.primary')}
                    onPrimaryAction={onOpenChange}
                />
            )}

            {/* Delete Dialog */}
            {action === 'delete' && (
                <FormDialog
                    open={true}
                    onOpenChange={onOpenChange}
                    title={t('projects.list.dialogs.delete.title')}
                    description={t('projects.list.dialogs.delete.description')}
                    primaryActionLabel={t('projects.list.dialogs.delete.primary')}
                    onPrimaryAction={handleDelete}
                    primaryActionVariant="destructive"
                    contentClassName="text-center"
                    descriptionClassName="text-secondary-foreground"
                />
            )}
        </>
    )
})
